import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, X, FileText, Image, Video, FileAudio, Link, CheckCircle, AlertCircle, AlertTriangle, FolderTree } from 'lucide-react';
import { useFileStore } from '../../stores/file-store';
import { useTagStore } from '../../stores/tag-store';
import { formatFileSize } from '../../utils/file-types';
import { Tag, Category, PermissionLevel } from '../../types';
import { useI18n } from '../../i18n';
import CategorySelector from '../categories/CategorySelector';
import { useCategoryStore } from '../../stores/category-store';
import { supabase } from '../../config/supabase';

// 带路径信息的文件类型
interface FileWithPath {
  file: File;
  relativePath: string;  // 如 'Marketing Promotion/DCI/cert.png'
  folderPath: string;    // 如 'Marketing Promotion/DCI'
}

interface UploadModalProps {
  category?: Category;
  onClose: () => void;
}

const UploadModal: React.FC<UploadModalProps> = ({ category, onClose }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [filesWithPath, setFilesWithPath] = useState<FileWithPath[]>([]); // 带路径的文件
  const [hasFolderStructure, setHasFolderStructure] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [fileUrl, setFileUrl] = useState('');
  const [fileName, setFileName] = useState('');
  const [isUrlUploading, setIsUrlUploading] = useState(false);
  const [urlError, setUrlError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const { uploadFiles, uploadFromUrl } = useFileStore();
  const { createCategory } = useCategoryStore();
  const [progress, setProgress] = useState<{ processed: number; total: number; startTime?: number; phase?: string } | null>(null);
  const { tags, fetchTags } = useTagStore();
  const { t } = useI18n();
  const { categories, fetchCategories } = useCategoryStore();

  // 权限设置（上传时）
  const [ownerOnly, setOwnerOnly] = useState<boolean>(false);
  const [isPublic, setIsPublic] = useState<boolean>(false);
  const [publicExpiresAt, setPublicExpiresAt] = useState<string>('');
  const [minPermissionLevel, setMinPermissionLevel] = useState<PermissionLevel>('basic');

  // 两步上传：先选文件夹，再上传
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(category?.id || null);
  const [step, setStep] = useState<'select' | 'upload'>(category ? 'upload' : 'select');

  // 🔍 重复文件检测
  const [existingFileNames, setExistingFileNames] = useState<Set<string>>(new Set());
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);

  // 🔴 内部错误弹窗
  const [errorModal, setErrorModal] = useState<{ show: boolean; title: string; message: string }>({
    show: false,
    title: '',
    message: ''
  });

  // 当URL改变时自动提取文件名
  useEffect(() => {
    if (fileUrl) {
      try {
        const url = new URL(fileUrl);
        const pathSegments = url.pathname.split('/');
        const defaultName = pathSegments[pathSegments.length - 1];
        // 解码URL编码的文件名
        const decodedName = decodeURIComponent(defaultName);
        setFileName(decodedName || '');
      } catch (error) {
        console.warn('Invalid URL or no filename found');
        setFileName('');
      }
    } else {
      setFileName('');
    }
  }, [fileUrl]);
  
  useEffect(() => {
    fetchTags();
  }, [fetchTags]);
  
  // 确保有分类数据用于选择
  useEffect(() => {
    if (!categories || categories.length === 0) {
      try { fetchCategories(); } catch {}
    }
  }, [categories, fetchCategories]);

  // 🔍 检测重复文件：当分类ID变化时，查询该分类下已有的所有文件名（支持分页获取所有记录）
  const checkDuplicates = useCallback(async (catId: string | null) => {
    if (!catId) {
      setExistingFileNames(new Set());
      return;
    }
    
    setIsCheckingDuplicates(true);
    try {
      // 使用分页获取所有文件名，突破 1000 条限制
      const PAGE_SIZE = 1000;
      const names = new Set<string>();
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const { data: existingFiles, error } = await supabase
          .from('file_categories')
          .select('file:files!inner(name, deleted_at)')
          .eq('category_id', catId)
          .is('file.deleted_at', null)
          .range(offset, offset + PAGE_SIZE - 1);
        
        if (error) {
          console.warn('检测重复文件失败:', error);
          break;
        }

        if (!existingFiles || existingFiles.length === 0) {
          hasMore = false;
        } else {
          for (const fc of existingFiles) {
            const fileName = (fc.file as any)?.name;
            if (fileName) {
              names.add(fileName);
            }
          }
          
          offset += existingFiles.length;
          
          // 如果返回的数据少于 PAGE_SIZE，说明没有更多数据了
          if (existingFiles.length < PAGE_SIZE) {
            hasMore = false;
          }
        }
      }

      setExistingFileNames(names);
      console.log(`📁 当前分类已有 ${names.size} 个文件（分页获取完成）`);
    } catch (err) {
      console.warn('检测重复文件失败:', err);
    } finally {
      setIsCheckingDuplicates(false);
    }
  }, []);

  // 当分类变化时检测重复
  useEffect(() => {
    const targetCatId = category?.id || selectedCategoryId;
    checkDuplicates(targetCatId);
  }, [category?.id, selectedCategoryId, checkDuplicates]);
  
  // 统计新文件和重复文件数量
  const duplicateFiles = files.filter(f => existingFileNames.has(f.name));
  const newFilesCount = files.length - duplicateFiles.length;

  
  // 🚫 过滤隐藏文件和系统文件
  const filterSystemFiles = useCallback((fileList: File[]): File[] => {
    const systemFilePatterns = [
      /^\.DS_Store$/i,
      /^\.WeDrive$/i,
      /^Thumbs\.db$/i,
      /^desktop\.ini$/i,
      /^\.Spotlight-V100$/i,
      /^\.Trashes$/i,
      /^\.fseventsd$/i,
      /^\.TemporaryItems$/i,
      /^__MACOSX/i,
      /^\.git$/i,
      /^\.svn$/i,
      /^\.hg$/i,
      /^~\$/,  // Office 临时文件
    ];
    
    return fileList.filter(file => {
      const fileName = file.name;
      // 过滤以点开头的隐藏文件（但保留一些常见的有效文件类型）
      const isHiddenFile = fileName.startsWith('.') && 
        !fileName.match(/^\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|md|jpg|jpeg|png|gif|mp4|mp3)$/i);
      
      // 检查是否匹配系统文件模式
      const isSystemFile = systemFilePatterns.some(pattern => pattern.test(fileName));
      
      if (isHiddenFile || isSystemFile) {
        console.log(`🚫 过滤系统/隐藏文件: ${fileName}`);
        return false;
      }
      return true;
    });
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const items = Array.from(e.dataTransfer.items);
    const droppedWithPath: FileWithPath[] = [];
    let hasSubFolders = false;
    
    const processEntry = async (entry: FileSystemEntry, parentPath = '') => {
      if (entry.isFile) {
        const file = await new Promise<File>((resolve) => {
          (entry as FileSystemFileEntry).file(resolve);
        });
        const relativePath = parentPath ? `${parentPath}/${entry.name}` : entry.name;
        const folderPath = parentPath || '';
        droppedWithPath.push({ file, relativePath, folderPath });
      } else if (entry.isDirectory) {
        hasSubFolders = true;
        const reader = (entry as FileSystemDirectoryEntry).createReader();
        // readEntries 可能分批返回，需要循环调用
        const allEntries: FileSystemEntry[] = [];
        let batch: FileSystemEntry[];
        do {
          batch = await new Promise<FileSystemEntry[]>((resolve) => reader.readEntries(resolve));
          allEntries.push(...batch);
        } while (batch.length > 0);
        const dirPath = parentPath ? `${parentPath}/${entry.name}` : entry.name;
        await Promise.all(allEntries.map(e => processEntry(e, dirPath)));
      }
    };
    
    Promise.all(items.map(item => {
      const entry = item.webkitGetAsEntry();
      return entry ? processEntry(entry) : Promise.resolve();
    })).then(() => {
      const filtered = droppedWithPath.filter(fp => {
        const result = filterSystemFiles([fp.file]);
        return result.length > 0;
      });
      if (hasSubFolders && filtered.some(f => f.folderPath)) {
        setFilesWithPath(prev => [...prev, ...filtered]);
        setHasFolderStructure(true);
        // 同步更新 files 用于计数
        setFiles(prev => [...prev, ...filtered.map(f => f.file)]);
      } else {
        setFiles(prev => [...prev, ...filtered.map(f => f.file)]);
      }
    });
  };
  
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };
  
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      const filteredFiles = filterSystemFiles(selectedFiles);
      setFiles(prev => [...prev, ...filteredFiles]);
    }
  };
  
  const handleFolderSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      const filteredFiles = filterSystemFiles(selectedFiles);
      
      // 检查是否包含文件夹结构（webkitRelativePath 含有 /）
      const hasStructure = filteredFiles.some(f => f.webkitRelativePath && f.webkitRelativePath.includes('/'));
      
      if (hasStructure) {
        const withPath: FileWithPath[] = filteredFiles.map(f => {
          const rp = f.webkitRelativePath || f.name;
          const parts = rp.split('/');
          // 去掉文件名，保留路径（可能有多级子文件夹）
          const folderPath = parts.slice(0, -1).join('/');
          return { file: f, relativePath: rp, folderPath };
        });
        setFilesWithPath(prev => [...prev, ...withPath]);
        setHasFolderStructure(true);
      }
      
      setFiles(prev => [...prev, ...filteredFiles]);
    }
  };
  
  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    if (hasFolderStructure) {
      setFilesWithPath(prev => prev.filter((_, i) => i !== index));
    }
  };

  // 解析文件夹结构并统计
  const folderStructure = React.useMemo(() => {
    if (!hasFolderStructure || filesWithPath.length === 0) return null;
    const folders = new Map<string, number>(); // folderPath -> fileCount
    for (const fp of filesWithPath) {
      if (fp.folderPath) {
        folders.set(fp.folderPath, (folders.get(fp.folderPath) || 0) + 1);
      }
    }
    return folders;
  }, [hasFolderStructure, filesWithPath]);

  // 递归创建文件夹结构并上传文件
  const uploadWithFolderStructure = async () => {
    const targetCategoryId = category?.id || selectedCategoryId || undefined;
    if (!targetCategoryId) return;

    const startTime = Date.now();
    const totalFiles = files.length;
    let processed = 0;

    // Step 1: 解析需要创建的文件夹路径
    const allFolderPaths = new Set<string>();
    for (const fp of filesWithPath) {
      if (fp.folderPath) {
        // 添加每一级子路径
        const parts = fp.folderPath.split('/');
        for (let i = 0; i < parts.length; i++) {
          allFolderPaths.add(parts.slice(0, i + 1).join('/'));
        }
      }
    }

    setProgress({ processed: 0, total: totalFiles, startTime, phase: '创建文件夹结构...' });

    // Step 2: 按层级顺序创建文件夹
    const sortedPaths = Array.from(allFolderPaths).sort((a, b) => a.split('/').length - b.split('/').length);
    const pathToCategoryId = new Map<string, string>();
    pathToCategoryId.set('', targetCategoryId); // 根目录 = 目标分类

    for (const folderPath of sortedPaths) {
      const parts = folderPath.split('/');
      const folderName = parts[parts.length - 1];
      const parentPath = parts.slice(0, -1).join('/');
      const parentId = pathToCategoryId.get(parentPath) || targetCategoryId;

      try {
        // 先检查是否已存在同名子分类
        const { data: existing } = await supabase
          .from('categories')
          .select('id')
          .eq('name', folderName)
          .eq('parent_id', parentId)
          .maybeSingle();

        if (existing) {
          pathToCategoryId.set(folderPath, existing.id);
        } else {
          const created = await createCategory(folderName, parentId);
          pathToCategoryId.set(folderPath, created.id);
        }
      } catch (err) {
        console.error(`创建文件夹失败: ${folderPath}`, err);
        // 回退：使用父级分类
        pathToCategoryId.set(folderPath, parentId);
      }
    }

    // Step 3: 按文件夹分组上传
    const filesByFolder = new Map<string, File[]>();
    for (const fp of filesWithPath) {
      const catId = pathToCategoryId.get(fp.folderPath) || targetCategoryId;
      if (!filesByFolder.has(catId)) filesByFolder.set(catId, []);
      filesByFolder.get(catId)!.push(fp.file);
    }
    // 也包括没有路径的普通文件
    const filesWithoutPath = files.filter(f => !filesWithPath.some(fp => fp.file === f));
    if (filesWithoutPath.length > 0) {
      if (!filesByFolder.has(targetCategoryId)) filesByFolder.set(targetCategoryId, []);
      filesByFolder.get(targetCategoryId)!.push(...filesWithoutPath);
    }

    setProgress({ processed: 0, total: totalFiles, startTime, phase: '上传文件中...' });

    // Step 4: 逐文件夹上传
    for (const [catId, catFiles] of filesByFolder) {
      await uploadFiles(catFiles, selectedTags, catId, (p, t) => {
        processed = processed + 1;
        setProgress({ processed, total: totalFiles, startTime, phase: '上传文件中...' });
      }, {
        permissionLevel: minPermissionLevel,
        ownerOnly,
        isPublic,
        publicExpiresAt
      });
      processed = processed + catFiles.length - (catFiles.length); // 已被 onProgress 更新
    }

    await fetchCategories();
  };
  
  const handleUpload = async () => {
    try {
      setUploading(true);
      setUrlError('');
      const targetCategoryId = category?.id || selectedCategoryId || undefined;
      if (!targetCategoryId) {
        setUrlError(t('selectDestinationFolder'));
        setUploading(false);
        return;
      }
      
      // 上传本地文件
      if (files.length > 0) {
        if (hasFolderStructure && filesWithPath.length > 0) {
          // 递归文件夹上传
          await uploadWithFolderStructure();
        } else {
          // 普通扁平上传
          const startTime = Date.now();
          setProgress({ processed: 0, total: files.length, startTime });
          await uploadFiles(files, selectedTags, targetCategoryId, (processed, total) => {
            setProgress({ processed, total, startTime });
          }, {
            permissionLevel: minPermissionLevel,
            ownerOnly,
            isPublic,
            publicExpiresAt
          });
        }
      }
      
      // 上传URL文件
      if (fileUrl.trim() && fileName.trim()) {
        await uploadFromUrl(fileUrl, fileName, selectedTags, targetCategoryId, {
          permissionLevel: minPermissionLevel,
          ownerOnly,
          isPublic,
          publicExpiresAt
        });
      }
      
      console.log('上传成功完成');
      
      // 🔄 刷新分类数据，使当前页面立即显示新上传的文件
      await fetchCategories();
      
      onClose();
    } catch (error: any) {
      console.error('上传失败:', {
        error,
        errorMessage: error.message,
        errorStack: error.stack,
        files: files.map(f => ({ name: f.name, size: f.size, type: f.type }))
      });
      // 显示内部错误弹窗
      setErrorModal({
        show: true,
        title: '上传失败',
        message: error.message || '请检查网络连接后重试'
      });
    } finally {
      setUploading(false);
      setProgress(null);
    }
  };

  const handleUrlUpload = async () => {
    if (!fileUrl.trim()) {
      setUrlError(t('enterFileUrl'));
      return;
    }

    if (!fileName.trim()) {
      setUrlError(t('enterFilename'));
      return;
    }

    try {
      setIsUrlUploading(true);
      setUrlError('');
      const targetCategoryId = category?.id || selectedCategoryId || undefined;
      if (!targetCategoryId) {
        setUrlError(t('selectDestinationFolder'));
        setIsUrlUploading(false);
        return;
      }
      await uploadFromUrl(fileUrl, fileName, selectedTags, targetCategoryId);
      console.log('URL文件上传成功');
      
      // 🔄 刷新分类数据
      await fetchCategories();
      
      onClose();
    } catch (error: any) {
      console.error('URL文件上传失败:', error);
      setUrlError(t('uploadFailedPrefix') + (error.message || '请稍后重试'));
    } finally {
      setIsUrlUploading(false);
    }
  };
  
  const getFileIcon = (file: File) => {
    const type = file.type;
    if (type.startsWith('image/')) return Image;
    if (type.startsWith('video/')) return Video;
    if (type.startsWith('audio/')) return FileAudio;
    return FileText;
  };
  
  const toggleTag = (tag: Tag) => {
    setSelectedTags(prev => 
      prev.some(t => t.id === tag.id)
        ? prev.filter(t => t.id !== tag.id)
        : [...prev, tag]
    );
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-4xl bg-white rounded-lg shadow-xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b sticky top-0 z-10 bg-white">
          <div>
            <h2 className="text-lg font-medium text-gray-800">{t('uploadTitle')}</h2>
            {step === 'upload' && (category?.name || selectedCategoryId) && (
              <p className="text-sm text-gray-500">
                {t('uploadTo')}: {category?.name || (function getName(nodes: Category[] | undefined, id: string | null): string {
                  if (!nodes || !id) return '';
                  const stack: (Category & { children?: Category[] })[] = [...(nodes as any[])];
                  while (stack.length) {
                    const n = stack.pop()!;
                    if (n.id === id) return n.name;
                    if (n.children) stack.push(...(n.children as any[]));
                  }
                  return '';
                })(categories as any[], selectedCategoryId)}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 rounded-full hover:bg-gray-100"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Step 1: 选择文件夹 */}
        {step === 'select' && !category && (
          <div className="px-4 pt-4 pb-2">
            <p className="text-sm text-gray-600 mb-2">{t('selectDestinationFolder')}</p>
            <CategorySelector
              categories={(categories as any[]) || []}
              value={selectedCategoryId || ''}
              onChange={(id) => setSelectedCategoryId(id)}
              enableCreate
              className="border border-gray-200 rounded-md p-3"
            />
          </div>
        )}

        {step === 'upload' && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className="p-6"
        >
          <div className={`flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg transition-colors ${isDragging ? 'border-teal-500 bg-teal-50' : 'border-gray-300 hover:border-teal-500'}`}>
            <Upload size={32} className="mb-3 text-gray-400" />
            <p className="mb-1 text-sm font-medium text-gray-700">
              拖拽文件或文件夹到这里
            </p>
            <p className="mb-4 text-xs text-gray-400">
              支持文件夹拖拽，自动保留层级结构
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
              >
                <FileText size={15} />
                选择文件
              </button>
              <button
                onClick={() => folderInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-teal-700 bg-teal-50 border border-teal-300 rounded-lg hover:bg-teal-100 hover:border-teal-400 transition-colors shadow-sm"
              >
                <FolderTree size={15} />
                选择文件夹
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <input
              ref={folderInputRef}
              type="file"
              multiple
              // @ts-ignore 非标准属性用于选择文件夹
              webkitdirectory=""
              onChange={handleFolderSelect}
              className="hidden"
            />
          </div>

          <div className="mt-6 border-t pt-6">
            <h3 className="mb-3 text-sm font-medium text-gray-700 flex items-center">
              <Link size={16} className="mr-1" />
              {t('uploadFromUrl')}
            </h3>
            <div className="flex space-x-2">
              <input
                type="text"
                value={fileUrl}
                onChange={(e) => setFileUrl(e.target.value)}
                placeholder={t('urlPlaceholder')}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>
            <div className="mt-2">
              <input
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder={t('filenamePlaceholder')}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>
            <div className="mt-2 flex space-x-2">
              <button
                onClick={handleUrlUpload}
                disabled={isUrlUploading || !fileUrl.trim() || !fileName.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUrlUploading ? t('uploading') : t('upload')}
              </button>
            </div>
            {urlError && (
              <p className="mt-1 text-xs text-red-500">{urlError}</p>
            )}
          </div>
          
          {files.length > 0 && (
            <div className="mt-6">
              {/* 文件夹结构预览 */}
              {hasFolderStructure && folderStructure && folderStructure.size > 0 && (
                <div className="mb-4 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <FolderTree size={16} className="text-teal-600" />
                    <span className="text-sm font-medium text-teal-700">将创建 {folderStructure.size} 个子文件夹</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {Array.from(folderStructure.entries()).slice(0, 15).map(([path, count]) => (
                      <span key={path} className="inline-flex items-center px-2 py-0.5 text-[11px] bg-white border border-teal-200 rounded text-teal-700">
                        {path} <span className="ml-1 text-teal-500">({count})</span>
                      </span>
                    ))}
                    {folderStructure.size > 15 && (
                      <span className="text-[11px] text-teal-500">+{folderStructure.size - 15} 更多...</span>
                    )}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">
                  {t('selectedFiles')} ({files.length})
                </h3>
                {/* 📊 重复文件统计 */}
                {existingFileNames.size > 0 && (
                  <div className="flex items-center gap-3 text-xs">
                    {isCheckingDuplicates ? (
                      <span className="text-gray-500">检测中...</span>
                    ) : (
                      <>
                        <span className="flex items-center text-green-600">
                          <CheckCircle size={14} className="mr-1" />
                          新文件 {newFilesCount}
                        </span>
                        {duplicateFiles.length > 0 && (
                          <span className="flex items-center text-amber-600">
                            <AlertCircle size={14} className="mr-1" />
                            已存在 {duplicateFiles.length}（将跳过）
                          </span>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1">
                {files.map((file, index) => {
                  const FileIcon = getFileIcon(file);
                  const isDuplicate = existingFileNames.has(file.name);
                  return (
                    <div
                      key={index}
                      className={`flex items-center p-3 rounded-lg ${
                        isDuplicate 
                          ? 'bg-amber-50 border border-amber-200' 
                          : 'bg-gray-50'
                      }`}
                    >
                      <div className={`p-2 mr-3 rounded-md ${isDuplicate ? 'bg-amber-100' : 'bg-white'}`}>
                        <FileIcon size={20} className={isDuplicate ? 'text-amber-500' : 'text-gray-500'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <p className={`text-sm font-medium truncate ${isDuplicate ? 'text-amber-700' : 'text-gray-800'}`}>
                            {file.name}
                          </p>
                          {isDuplicate && (
                            <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 bg-amber-200 rounded">
                              已存在
                            </span>
                          )}
                        </div>
                        {hasFolderStructure && filesWithPath[index]?.folderPath && (
                          <p className="text-[11px] text-teal-600 truncate flex items-center gap-1">
                            <FolderTree size={10} />
                            {filesWithPath[index].folderPath}
                          </p>
                        )}
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        className="p-1 ml-2 text-gray-400 rounded-full hover:bg-gray-200 hover:text-gray-600"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
              {/* 提示信息 */}
              {duplicateFiles.length > 0 && (
                <p className="mt-2 text-xs text-amber-600 flex items-center">
                  <AlertCircle size={12} className="mr-1" />
                  已存在的 {duplicateFiles.length} 个文件将被跳过，不会重复上传
                </p>
              )}
            </div>
          )}
          
          <div className="mt-6">
            {/* 权限设置 */}
            <h3 className="mb-3 text-sm font-medium text-gray-700">权限设置</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm text-gray-700">私密（仅自己可见）</label>
                  <button
                    onClick={() => { const next = !ownerOnly; setOwnerOnly(next); if (next) setIsPublic(false); }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full ${ownerOnly ? 'bg-teal-600' : 'bg-gray-200'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${ownerOnly ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                <p className="text-xs text-gray-500">开启后，仅创建者可见，不生成公开链接。</p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm text-gray-700">公开访问</label>
                  <button
                    disabled={ownerOnly}
                    onClick={() => !ownerOnly && setIsPublic(v => !v)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full ${ownerOnly ? 'bg-gray-200 opacity-60 cursor-not-allowed' : (isPublic ? 'bg-teal-600' : 'bg-gray-200')}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${(isPublic && !ownerOnly) ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                {isPublic && !ownerOnly && (
                  <input
                    type="date"
                    value={publicExpiresAt}
                    onChange={(e) => setPublicExpiresAt(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="mt-2 w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500"
                  />
                )}
              </div>
            </div>

            {!ownerOnly && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">最低访问权限级别</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {(['basic','intermediate','advanced','admin'] as PermissionLevel[]).map(level => (
                    <button
                      key={level}
                      onClick={() => setMinPermissionLevel(level)}
                      className={`p-2 text-left rounded border ${minPermissionLevel===level ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <div className="text-sm font-medium capitalize">{level}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="mt-6">
            <h3 className="mb-3 text-sm font-medium text-gray-700">{t('tags')}</h3>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => toggleTag(tag)}
                  className={`flex items-center px-3 py-1.5 text-sm rounded-full transition-colors ${
                    selectedTags.some(t => t.id === tag.id)
                      ? 'bg-teal-100 text-teal-800'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span
                    className="w-2 h-2 mr-2 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  ></span>
                  {tag.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        )}
        
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t rounded-b-lg sticky bottom-0 z-10">
          {step === 'upload' && progress && (
            <div className="flex items-center gap-3 text-xs text-gray-600">
              <span>{progress.phase || '正在上传'} {progress.processed}/{progress.total}</span>
              {/* 预估剩余时间 */}
              {progress.processed > 0 && progress.startTime && (
                <span className="text-teal-600">
                  剩余约 {(() => {
                    const elapsed = Date.now() - progress.startTime;
                    const rate = progress.processed / (elapsed / 1000);
                    const remaining = progress.total - progress.processed;
                    const remainingSeconds = Math.ceil(remaining / rate);
                    if (remainingSeconds < 60) return `${remainingSeconds} 秒`;
                    if (remainingSeconds < 3600) return `${Math.ceil(remainingSeconds / 60)} 分钟`;
                    return `${Math.ceil(remainingSeconds / 3600)} 小时`;
                  })()}
                </span>
              )}
              {/* 进度条 */}
              <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-teal-500 transition-all duration-300"
                  style={{ width: `${(progress.processed / progress.total) * 100}%` }}
                />
              </div>
            </div>
          )}
          <div className="flex items-center">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 mr-2"
            >
              {t('cancel')}
            </button>
            {step === 'select' ? (
              <button
                onClick={() => setStep('upload')}
                disabled={!selectedCategoryId}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('next')}
              </button>
            ) : (
              <button
                onClick={handleUpload}
                disabled={(!category && !selectedCategoryId) || (files.length === 0 && (!fileUrl.trim() || !fileName.trim())) || uploading || isUrlUploading}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? t('uploading') : t('upload')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 🔴 内部错误弹窗 - Apple 风格 */}
      {errorModal.show && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div 
            className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200"
            style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
          >
            {/* 图标区域 */}
            <div className="flex flex-col items-center pt-8 pb-4">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {errorModal.title}
              </h3>
            </div>
            
            {/* 消息内容 */}
            <div className="px-6 pb-4">
              <p className="text-center text-sm text-gray-600 leading-relaxed">
                {errorModal.message}
              </p>
            </div>
            
            {/* 操作按钮 */}
            <div className="border-t border-gray-200">
              <button
                onClick={() => setErrorModal({ show: false, title: '', message: '' })}
                className="w-full py-3.5 text-base font-medium text-teal-600 hover:bg-gray-50 transition-colors"
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadModal;